import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ObjectIndex, FilterPill } from '@/components/shared/object-index';
import { Button } from '@/components/ui/button';
import { formatMoney } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useDeals, usePipelines } from './api';
import { OwnerPill } from './records';

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/**
 * Deals on a calendar by close date (HubSpot's calendar view): a month at a
 * time, so a manager sees what should land this week and what has slipped.
 */
export function DealsCalendarPage() {
  const t = useT();
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [pipelineId, setPipelineId] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [status, setStatus] = useState('OPEN');
  const { data: pipelines = [] } = usePipelines();

  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const filters = JSON.stringify({
    match: 'ALL',
    conditions: [
      { field: 'expectedCloseDate', op: 'after', value: iso(new Date(first.getTime() - 86_400_000)) },
      { field: 'expectedCloseDate', op: 'before', value: iso(new Date(last.getTime() + 86_400_000)) },
    ],
  });
  const { data } = useDeals({ filters, limit: 100, pipelineId: pipelineId || undefined, ownerId: ownerId || undefined, status: status || undefined, sort: 'expected_close_date' });

  const byDay = useMemo(() => {
    const map = new Map<string, NonNullable<typeof data>['data']>();
    for (const deal of data?.data ?? []) {
      if (!deal.expectedCloseDate) continue;
      const key = deal.expectedCloseDate.slice(0, 10);
      map.set(key, [...(map.get(key) ?? []), deal]);
    }
    return map;
  }, [data]);

  // Weeks start on Monday, as the working week does in the UAE office.
  const lead = (first.getDay() + 6) % 7;
  const cells: (Date | null)[] = [...Array.from({ length: lead }, () => null), ...Array.from({ length: last.getDate() }, (_, index) => new Date(month.getFullYear(), month.getMonth(), index + 1))];
  while (cells.length % 7) cells.push(null);
  const today = iso(new Date());
  const total = (data?.data ?? []).reduce((sum, deal) => sum + Number(deal.value ?? 0), 0);

  return (
    <ObjectIndex
      title="Deals"
      count={data?.meta?.total}
      mode="calendar"
      tableHref="/crm/deals/list"
      boardHref="/crm/deals"
      calendarHref="/crm/deals/calendar"
      filters={
        <>
          <FilterPill label="Pipeline" value={pipelineId} onChange={setPipelineId} options={pipelines.map((pipeline) => ({ value: pipeline.id, label: pipeline.name }))} />
          <OwnerPill label="Deal owner" value={ownerId} onChange={setOwnerId} />
          <FilterPill
            label="Status"
            value={status}
            onChange={setStatus}
            options={[
              { value: 'OPEN', label: t('Open') },
              { value: 'WON', label: t('Won') },
              { value: 'LOST', label: t('Lost') },
            ]}
          />
        </>
      }
      toolbarEnd={
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} aria-label={t('Previous month')}>
            <ChevronLeft />
          </Button>
          <span className="min-w-[8.5rem] text-center text-sm font-bold">{month.toLocaleDateString('en-AE', { month: 'long', year: 'numeric' })}</span>
          <Button size="sm" variant="outline" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} aria-label={t('Next month')}>
            <ChevronRight />
          </Button>
        </div>
      }
    >
      <div className="flex items-center justify-between px-4 py-2 text-sm text-muted-foreground">
        <span>
          <span className="tabular font-semibold text-foreground">{formatMoney(total.toFixed(2), 'AED')}</span> {t('closing this month')}
        </span>
        <Link to="/crm/deals/list" className="text-link text-xs font-semibold hover:underline">
          {t('Open the list view')}
        </Link>
      </div>
      <div className="overflow-x-auto">
        <div className="grid min-w-[56rem] grid-cols-7 border-t">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
            <div key={day} className="border-b border-e bg-muted/50 px-2 py-1.5 text-xs font-bold text-muted-foreground last:border-e-0">
              {t(day)}
            </div>
          ))}
          {cells.map((date, index) => {
            const key = date ? iso(date) : `blank-${index}`;
            const deals = date ? (byDay.get(iso(date)) ?? []) : [];
            return (
              <div key={key} className={cn('min-h-[7.5rem] border-b border-e p-1.5 [&:nth-child(7n)]:border-e-0', !date && 'bg-muted/30')}>
                {date && (
                  <>
                    <div className={cn('mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold', iso(date) === today ? 'bg-primary text-white' : 'text-muted-foreground')}>{date.getDate()}</div>
                    <div className="space-y-1">
                      {deals.slice(0, 4).map((deal) => (
                        <Link
                          key={deal.id}
                          to={`/crm/deals/${deal.id}`}
                          className="block truncate rounded px-1.5 py-1 text-[11px] font-semibold text-white hover:opacity-90"
                          style={{ backgroundColor: deal.stage.colour }}
                          title={`${deal.title} · ${formatMoney(deal.value, deal.currency)}`}
                        >
                          {deal.title}
                        </Link>
                      ))}
                      {deals.length > 4 && <p className="px-1 text-[11px] text-muted-foreground">+{deals.length - 4} {t('more')}</p>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </ObjectIndex>
  );
}
